// Account (SERVER.md §1, §5): sign in with Google (Google Identity Services) or, against a local server with
// DEV_LOGIN=1, the dev sign-in; `/health` says which providers have keys, `/v1/me` which features the account has.
import { API_BASE } from "../config";
import { clearAll } from "../store/db";
import { Hub } from "../util";
import { api, errorBody, problem, problemMessage, session, setSignedOutHandler, type Provider } from "./client";

export interface User { id: number; email: string | null; admin: boolean }
export interface Features { sync: boolean; fetch: boolean }

class Account {
  user: User | null = null;
  features: Features = { sync: false, fetch: false };
  providers: Set<string> | null = null;
  devLogin = false;
  /** Accounts with `features.chooseModels` pick their own models; others use the server's `models`. Missing = true. */
  canChooseModels = true;
  serverModels: Record<string, any> = {};
  /** "standard" | "plus" (`/v1/me` plan), when the server says. */
  plan: string | null = null;
  reachable = true;
  readonly events = new Hub<{ changed: void }>();

  constructor() {
    setSignedOutHandler(() => this.signOut(false));
    try {
      const saved = JSON.parse(localStorage.getItem("depth.account:" + API_BASE) || "null");
      if (saved && session.token) {
        this.user = saved.user; this.features = saved.features;
        this.canChooseModels = saved.canChooseModels !== false; this.serverModels = saved.serverModels ?? {};
      }
    } catch { /* ignore */ }
  }

  get signedIn(): boolean { return !!session.token && !!this.user; }

  available(p: Provider): boolean { return this.signedIn && (this.providers?.has(p) ?? true); }

  unavailableReason(p: Provider): string | null {
    if (!this.signedIn) return problemMessage({ kind: "signedOut" });
    if (this.providers && !this.providers.has(p)) {
      const name = { openrouter: "OpenRouter", anthropic: "Anthropic", openai: "OpenAI", gemini: "Gemini", typesafe: "TypeSafe" }[p];
      return `${name} isn't available on this server.`;
    }
    return null;
  }

  private persist() {
    try {
      if (this.user) localStorage.setItem("depth.account:" + API_BASE, JSON.stringify({ user: this.user, features: this.features,
        canChooseModels: this.canChooseModels, serverModels: this.serverModels }));
      else localStorage.removeItem("depth.account:" + API_BASE);
    } catch { /* ignore */ }
  }

  /** `/health` (providers, dev login) and, signed in, `/v1/me`. */
  async refresh(): Promise<void> {
    try {
      const h = await fetch(API_BASE + "/health").then((r) => r.json());
      this.providers = new Set(h.providers ?? []);
      this.devLogin = !!h.devLogin;
      this.reachable = true;
    } catch {
      this.reachable = false;
    }
    if (session.token) {
      try {
        const r = await api("/v1/me");
        if (r.ok) {
          const me = await r.json();
          this.user = me.user;
          this.features = { sync: !!me.features?.sync, fetch: !!me.features?.fetch };
          // features.chooseModels (missing = true); an older server's top-level canChooseModels is read too.
          this.canChooseModels = me.features?.chooseModels !== false && me.canChooseModels !== false;
          this.plan = typeof me.plan === "string" ? me.plan : null;
          this.serverModels = me.models && typeof me.models === "object" ? me.models : {};
          this.persist();
        }
      } catch { /* offline: keep the saved account */ }
    }
    this.events.emit("changed", undefined);
  }

  private async finish(res: Response): Promise<void> {
    if (!res.ok) {
      const { json, text } = await errorBody(res);
      const p = problem(res.status, json);
      throw new Error(p ? problemMessage(p) : json?.error === "invalid_token" ? "Google's sign-in couldn't be verified."
        : json?.error === "invalid_code" ? "That code is wrong, used or expired. Make a new one in Depth's Settings on a signed-in device." : `Sign-in failed (HTTP ${res.status}). ${text.slice(0, 120)}`);
    }
    const data = await res.json();
    const previous = localStorage.getItem("depth.lastUser:" + API_BASE);
    session.set(data.token);
    this.user = data.user;
    if (previous && previous !== String(data.user.id)) {
      // Another account's library must never show up here.
      await clearAll();
      try { for (const k of Object.keys(localStorage)) if (k.startsWith("depth.state.")) localStorage.removeItem(k); } catch { /* ignore */ }
      localStorage.setItem("depth.lastUser:" + API_BASE, String(data.user.id));
      this.persist();
      location.hash = "#/";
      location.reload();
      return;
    }
    localStorage.setItem("depth.lastUser:" + API_BASE, String(data.user.id));
    await this.refresh();
  }

  async signInWithGoogle(idToken: string): Promise<void> {
    await this.finish(await api("/v1/auth/google", { method: "POST", json: { idToken }, auth: false }));
  }

  /** A one-time code from Settings → Account → Link a device on a signed-in device (SERVER.md §1). */
  async signInWithCode(code: string): Promise<void> {
    await this.finish(await api("/v1/auth/link", { method: "POST", json: { code: code.trim() }, auth: false }));
  }

  async devSignIn(email: string): Promise<void> {
    await this.finish(await api("/v1/auth/dev", { method: "POST", json: { email }, auth: false }));
  }

  async signOut(tellServer = true): Promise<void> {
    if (tellServer && session.token) await api("/v1/auth/logout", { method: "POST" }).catch(() => {});
    session.set(null);
    this.user = null;
    this.features = { sync: false, fetch: false };
    this.canChooseModels = true;
    this.serverModels = {};
    this.persist();
    this.events.emit("changed", undefined);
  }
}

export const account = new Account();
